<template>
  <div>
    <DxForm id="formularioEmpresaServicios" ref="formInstance" :form-data="model" label-location="top"
      :show-colon-after-label="false" :visible="model != null" validation-group="validacionEmpresaServicios">
      <!-- xs: 768, sm: 992, md:1200 -->
      <DxColCountByScreen :xs="6" :sm="6" :md="6" :lg="6" />

      <!-- Datos de la empresa -->
      <DxGroupItem :col-span="6" :col-count="6">
        <DxSimpleItem data-field="Nombre" editor-type="dxTextBox" :col-span="6">
          <DxLabel :text="$t('empresas_de_servicio.form.nombre')" />
          <DxRequiredRule :message="$t('empresas_de_servicios.form.validacion.nombre')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="NombreContacto" editor-type="dxTextBox" :col-span="3">
          <DxLabel :text="$t('empresas_de_servicio.form.nombre_contacto')" />
          <DxRequiredRule :message="$t('empresas_de_servicios.form.validacion.nombre_contacto')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Activado" editor-type="dxRadioGroup" :col-span="3" :editor-options="radioGroupEstado">
          <DxLabel :text="$t('empresas_de_servicio.form.estado')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="EmailContacto" editor-type="dxTextBox" :col-span="3">
          <DxLabel :text="$t('empresas_de_servicio.form.email_contacto')" />
          <DxRequiredRule :message="$t('empresas_de_servicios.form.validacion.email_contacto')" />
          <DxEmailRule :message="$t('empresas_de_servicios.form.validacion.email_patron')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="TelContacto" editor-type="dxTextBox" :col-span="3">
          <DxLabel :text="$t('empresas_de_servicio.form.telefono_contacto')" />
          <DxRequiredRule :message="$t('empresas_de_servicios.form.validacion.telefono_contacto')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="DireccionContacto" editor-type="dxTextBox" :col-span="3">
          <DxLabel :text="$t('empresas_de_servicio.form.direccion_contacto')" />
          <DxRequiredRule :message="$t('empresas_de_servicios.form.validacion.direccion_contacto')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="IdGrupoEmpresa" editor-type="dxSelectBox" :col-span="3" 
          :editor-options="{ ...getSelectBoxDefaultProperties(), ...getSelectBoxFromApiEnum(GrupoEmpresaEnergiaEnum), searchEnabled: true, dropDownOptions: { maxHeight: 190 } }">
          <DxLabel :text="$t('empresas_de_servicio.form.grupo_empresa_energia')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="SincronizaEsave" editor-type="dxRadioGroup" :col-span="2" :editor-options="radioGroupSincronizaEsave">
          <DxLabel :text="$t('empresas_de_servicio.form.sincroniza_esave')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="IdEsave" editor-type="dxNumberBox" :col-span="1">
          <DxLabel :text="$t('empresas_de_servicio.form.idEsave')" />
          <DxCustomRule :validation-callback="validarIdEsave"
            :message="$t('empresas_de_servicio.form.validacion.idEsave')" />
        </DxSimpleItem>
        
        <DxSimpleItem data-field="IdsGestoresSoporte" editor-type="dxTagBox" :col-span="3"
          :editor-options="{ ...getSelectBoxDefaultProperties(), ...selectGestoresSoporteOptions }">
          <DxLabel :text="$t('empresas_de_servicio.form.gestores_soporte')" />
        </DxSimpleItem>
      </DxGroupItem>

      <!-- Imagen de la empresa -->
      <DxGroupItem :col-span="6" :col-count="6">
        <DxSimpleItem :col-span="3">
          <DxFileUploader :accept="'image/*'" :multiple="false" @valueChanged="onFileUploaderValueChanged">
            <DxValidator ref="validacionImagen" validation-group="validacionEmpresaServicios">
              <DxCustomRule :validation-callback="validarImagen"
                :message="$t('empresas_de_servicios.form.validacion.imagen')" />
            </DxValidator>
          </DxFileUploader>
          <DxLabel :text="$t('empresas_de_servicio.form.imagen')" />
        </DxSimpleItem>
        <DxSimpleItem :col-span="3">
          <div>
            <img v-if="props.model?.ImagenB64 != ''" :src="props.model?.ImagenB64" alt="Imagen de la empresa de servicios"
              style="max-width: 100%; margin-top: 10px;" />
          </div>
        </DxSimpleItem>
      </DxGroupItem>
    </DxForm>
  </div>
</template>

<script setup lang="ts">
import { messageBoxResult } from '@components/dialogs/message-box';
import { GrupoEmpresaEnergiaEnum, type CreateEmpresaServiciosModel, type EditEmpresaServiciosModel } from '@api/api';
import { useModalBuilder, type ToolbarReference } from '@helpers/modal-building-helper';
import { DxFileUploader, type DxButton } from 'devextreme-vue';
import {
  DxEmailRule,
  DxForm,
  DxLabel,
  DxColCountByScreen,
  DxRequiredRule,
  DxGroupItem,
  DxSimpleItem} from "devextreme-vue/form";
import DxValidator, { DxCustomRule } from 'devextreme-vue/validator';
import { getCurrentInstance, onBeforeMount, onMounted, reactive, ref } from 'vue';
import { useIntl } from '@services/i18n';
import { getLookup, getSelectBoxDefaultProperties } from '@helpers/grid-helper';
import validationEngine from "devextreme/ui/validation_engine";
import { useApi } from '@api/integration';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import { getSelectBoxFromApiEnum } from '@/.shared/helpers/enums-helper';

const api = useApi();
const formInstance = ref<DxForm>();
const intl = useIntl();
const validacionImagen = ref();
const props = defineProps({
  model: {
    type: Object as () => EditEmpresaServiciosModel | CreateEmpresaServiciosModel,
    required: false
  },
  isEditMode: {
    type: Boolean,
    required: true
  }
});

const selectGestoresSoporteOptions = reactive<any>({});

const radioGroupEstado = {
  items: [
    { text: intl.i18n.t('empresas_de_servicio.form.activado'), value: true },
    { text: intl.i18n.t('empresas_de_servicio.form.desactivado'), value: false }
  ],
  layout: "horizontal",
  valueExpr: "value"
}

const radioGroupSincronizaEsave = {
  items: [
    { text: intl.i18n.t('empresas_de_servicio.form.sincroniza_esave_si'), value: true },
    { text: intl.i18n.t('empresas_de_servicio.form.sincroniza_esave_no'), value: false }
  ],
  layout: "horizontal",
  valueExpr: "value"
}

function onFileUploaderValueChanged(e: any) {
  const file = e.value[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = function (event) {
      const base64 = (event.target as FileReader).result as string;
      if (props.model != null)
        props.model.ImagenB64 = base64;
    };
    reader.readAsDataURL(file); 
  }
};

onMounted(async () => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!)

  configureButtons(modal);
});

onBeforeMount(async () => {
  selectGestoresSoporteOptions.dataSource = await api.callStandardApi("getUsuariosBackofficeSelector", { showLoading: false });
});


function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());
  okButton.update(w => w.text = `${intl.i18n.t("shared.ui.buttons.save")}`);
  okButton.update(w => w.onClick = async () => {
    saveOrEdit().then((value) => { 
      if (value) 
        modal.close(messageBoxResult.ok) 
    });
  }
  );
  cancelButton.update(w => w.onClick = () => modal.close(messageBoxResult.cancel));
}

const saveOrEdit = async () => {
  var res = validationEngine.validateGroup("validacionEmpresaServicios");

  if (res.isValid) {
    if (props.isEditMode) {
      await api.callStandardApi("editEmpresaServicios", {showLoading: true}, props.model as EditEmpresaServiciosModel)
        .then(() => toastSuccess(intl.i18n.t('empresa_de_servicio.edicion_exito')))
        .catch(() => {
          toastError(intl.i18n.t('empresa_de_servicio.edicion_fallo'));
          return false;
        });
    } else {
      await api.callStandardApi("createEmpresaServicios", {showLoading: true}, props.model as CreateEmpresaServiciosModel)
        .then(() => toastSuccess(intl.i18n.t('empresa_de_servicio.creacion_exito')))
        .catch(() => {
          toastError(intl.i18n.t('empresa_de_servicio.creacion_fallo'));
          return false;
        });
    }
    return true;
  } else {
    toastError(intl.i18n.t('empresa_de_servicio.revise_validacion'));
  }

  return false;
}

function validarImagen(params: any) {
  return (props.model?.ImagenB64 && props.model?.ImagenB64 != '') || params.value.length != 0;
}

function validarIdEsave(params: any) {
  if (props.model?.SincronizaEsave) {
    return !!params.value;
  }
  return true;
}

</script>